import React from 'react';
import { withLocale, WithLocaleProps } from 'locale';

import { Section } from 'components/filters/styled/common';
import { SmallText, Text } from 'ds/components/typography';
import { Collapsable } from 'components/filters/common/Collapsable';
import { Tabs } from 'ds/components/tabs';
import { Title } from './Title';
import { Route } from './Route';
import {
  TransportationAccordionWrapper,
  SectionBody,
  CollapseButton,
  EmptySection,
  AccordionTabsHeader,
  SeeOnMapWrapper,
} from '../styled';
import { SectionWrapper, SectionType } from 'components/widget/common/styled';
import { WithCollapsables } from 'components/with-collapsibles';
import { ShowAt } from 'consts/breakpoints';
import { SectionIcon } from './SectionIcon';
import { TransportationSectionType } from 'store/state/insightsContext';
import { InsightType } from 'utils/entities';
import { SeeOnMapButton } from 'components/see-on-map-button';
import { TransportationAccordionSection, TransportationAccordionRoute } from 'store/state/selectors/insights/transportation';

interface TransportationAccordionProps {
  sections: TransportationAccordionSection[];
  activeSection: TransportationSectionType;
  setActiveSection: (section: TransportationSectionType) => void;
  activeTab: TransportationSectionType;
  setActiveTab: (tab: TransportationSectionType) => void;
  setHoveredStation: (name: string) => void;
  onSeeOnMapClick?: () => void;
  accordionItemsExist: boolean;
}

const TRANSIT_TABS = [ TransportationSectionType.Train, TransportationSectionType.Bus ];


const initiallyCollapsed = [
  TransportationSectionType.Bike,
  TransportationSectionType.Bus,
  TransportationSectionType.Train,
];

export const collapsedRoutesThreshold = 3;

class TransportationAccordion extends React.PureComponent<TransportationAccordionProps & WithLocaleProps> {

  public renderRoutes = (routes: TransportationAccordionRoute[], sectionId: TransportationSectionType) =>
    routes.map((route, id) => (
      <Route key={id} route={route} sectionId={sectionId} setHoveredStation={this.props.setHoveredStation} />
    ));

  public parseSection = (section: TransportationAccordionSection): JSX.Element => {
    const { t, activeTab, setActiveTab } = this.props;
    if ('routes' in section) {
      if (section.routes) {
        if (section.routes.length > collapsedRoutesThreshold) {
          return (
            <WithCollapsables<TransportationSectionType> initiallyCollapsed={initiallyCollapsed}>
              {({ collapsedSections, makeSectionToggler }) => {
                const isCollapsed = collapsedSections.has(section.sectionId);
                // if it is collapsed, do not render tail of the list
                const collapsedList = isCollapsed ? [] : section.routes.slice(collapsedRoutesThreshold);
                return (
                  <>
                    {this.renderRoutes(section.routes.slice(0, collapsedRoutesThreshold), section.sectionId)}
                    <Collapsable
                      isCollapsed={isCollapsed}
                      collapsedHeight={0}
                    >
                      {this.renderRoutes(collapsedList, section.sectionId)}
                    </Collapsable>
                    <CollapseButton type={InsightType.Transportation} onClick={makeSectionToggler(section.sectionId)}>
                      <SmallText style={{ cursor: 'pointer' }}>
                        {!collapsedSections.has(section.sectionId)
                          ? t('transportationAccordion.seeLess')
                          : t('transportationAccordion.seeAll', { amount: section.routes.length - collapsedRoutesThreshold })}
                      </SmallText>
                    </CollapseButton>
                  </>
                );
              }}
            </WithCollapsables>
          );
        }
        else {
          return (
            <div>
              {this.renderRoutes(section.routes, section.sectionId)}
            </div>
          );
        }
      }
      else {
        return (
          <EmptySection>
            <Text>{t('transportationAccordion.noInformation')}</Text>
          </EmptySection>
        );
      }
    }
    else if (section.tabs) {
      return (
        <Tabs
          activeTab={TRANSIT_TABS.indexOf(activeTab)}
          setActiveTab={(idx: number) => setActiveTab(TRANSIT_TABS[idx])}
          headerWrapper={AccordionTabsHeader}
          headers={section.tabs.map((tab: TransportationAccordionSection) => (
            <SectionType
              data-auto={tab.sectionId}
              style={{ minWidth: 'auto' }}
              key={tab.sectionId}
              isActive={tab.sectionId === activeTab}
              type={InsightType.Transportation}
            >
              <SectionIcon sectionId={tab.sectionId} />
              <ShowAt from={2}>
                <Text style={{ textTransform: 'capitalize' }}>
                  {t('transportationAccordion.sectionType', {
                    type: tab.sectionId,
                    routesAmount: 0,
                  })}
                </Text>
              </ShowAt>
              <ShowAt to={1}>
                <SmallText data-auto={tab.sectionId} style={{ textTransform: 'capitalize' }}>
                  {t('transportationAccordion.sectionType', {
                    type: tab.sectionId,
                    routesAmount: 0,
                  })}
                </SmallText>
              </ShowAt>
            </SectionType>
          ))}
          bodies={section.tabs.map(this.parseSection)}
        />
      );
    }
    else {
      return null;
    }
  };


  public render() {
    const { sections, activeSection, setActiveSection, onSeeOnMapClick, accordionItemsExist } = this.props;
    return (
      <TransportationAccordionWrapper>
        {sections.map(section => (
          <SectionWrapper
            data-auto={section.sectionId}
            type={InsightType.Transportation}
            className={activeSection === section.sectionId ? 'section-wrapper-active' : ''}
            key={section.sectionId}
          >
            <Section isOpen={activeSection === section.sectionId}>
              <Title
                section={section}
                activeSection={activeSection}
                toggleActiveSection={() => activeSection === section.sectionId ? undefined : setActiveSection(section.sectionId)}
              />
              <SectionBody type={InsightType.Transportation}>
                <ShowAt to={1}>
                  <Text style={{ marginBottom: '24px' }}>
                    <span dangerouslySetInnerHTML={{ __html: section.sectionResume }} />
                  </Text>
                </ShowAt>
                {this.parseSection(section)}
              </SectionBody>
            </Section>
          </SectionWrapper>
        ))}
        <ShowAt to={2}>
          {accordionItemsExist ? (
            <SeeOnMapWrapper>
              <SeeOnMapButton onClick={onSeeOnMapClick} type={InsightType.Transportation} />
            </SeeOnMapWrapper>
          ) : null}
        </ShowAt>
      </TransportationAccordionWrapper>
    );
  }
}

export default withLocale(TransportationAccordion);
